import { useState } from 'react'
import { useConfig, useSentinelChannels } from '@/api/queries'
import type { SentinelChannel } from '@/api/types'
import { DataTable, type Column } from '@/components/DataTable/DataTable'
import { Badge, ratioTone } from '@/components/ui/Badge'
import { Icons } from '@/components/ui/Icons'
import { Widget } from '@/components/ui/Widget'
import { compactNumber, percent } from '@/lib/format'
import { links } from '@/lib/links'
import { SentinelChannelDialog } from './SentinelChannelDialog'

export function SentinelSitesWidget({ className }: { className?: string }) {
  const { data, isLoading, error } = useSentinelChannels()
  const { data: config } = useConfig()
  const [selected, setSelected] = useState<SentinelChannel | null>(null)

  const columns: Column<SentinelChannel>[] = [
    {
      key: 'name',
      header: 'Site',
      render: (c) => (
        <button type="button" className="u-linkButton" onClick={() => setSelected(c)} title="Open hotel details">
          {c.name}
        </button>
      ),
    },
    { key: 'customers', header: 'Clients', align: 'right', hideBelow: 'sm' },
    { key: 'totalRequests', header: 'Requests', align: 'right', render: (c) => compactNumber(c.totalRequests) },
    {
      key: 'successRatio',
      header: 'Success',
      align: 'right',
      render: (c) => <Badge tone={ratioTone(c.successRatio, { warnBelow: 90, dangerBelow: 70 })}>{percent(c.successRatio, 1)}</Badge>,
    },
    { key: 'reviewIdRatio', header: 'Review id', align: 'right', hideBelow: 'md', title: 'reviewIdRatio: share of reviews with an id', render: (c) => percent(c.reviewIdRatio, 1) },
    { key: 'replied', header: 'Replied', align: 'right', hideBelow: 'md' },
    {
      key: 'grafana',
      header: '',
      sortable: false,
      width: '36px',
      render: (c) => {
        const href = links.grafanaSentinelSite(config?.links, c.name)
        return href ? (
          <a href={href} target="_blank" rel="noreferrer" title="Open in Grafana" className="u-row">
            <Icons.External size={14} />
          </a>
        ) : null
      },
    },
  ]

  return (
    <Widget title="Sentinel sites · last 24h" loading={isLoading} className={className}>
      <DataTable columns={columns} rows={data} rowKey={(c) => c.channelId} loading={isLoading} error={error} searchable searchPlaceholder="Search: facebook successRatio<90" dense initialSort={{ key: 'totalRequests', direction: 'desc' }} />
      {selected && <SentinelChannelDialog channel={selected} onClose={() => setSelected(null)} />}
    </Widget>
  )
}
